import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { rootReducersState } from "../../reducers";
import { similarJobsRequest } from "../../reducers/job/jobs.reducer";
import JobCards from "./JobCards";
import SimilarJobLoading from "./SimilarJobLoading";
import { Box, Typography } from "@material-ui/core";

const SIMILAR_JOBS_LIMIT = 4;
const SIMILAR_JOBS_FETCH_LIMIT = 10;

interface SimilarJobsProps {
  isJobHub?: boolean;
  token: string | null;
  /** Keep the skeleton up until the main list is ready to show as well. */
  hold?: boolean;
  onSettled?: () => void;
}

const SimilarJobs: React.FC<SimilarJobsProps> = ({
  isJobHub,
  token,
  hold = false,
  onSettled,
}) => {
  const dispatch = useDispatch();
  const jobState = useSelector((state: rootReducersState) => state.job.jobs);
  const similarJobs = useMemo(
    () => jobState?.similarJobs || [],
    [jobState?.similarJobs]
  );
  const similarJobsLoading = useMemo(
    () => jobState?.similarJobsLoading || false,
    [jobState?.similarJobsLoading]
  );
  const mainJobIds = useMemo(() => {
    const ids = new Set<number>();
    (jobState?.data || []).forEach((job: { id?: number }) => {
      const id = Number(job?.id);
      if (Number.isFinite(id)) ids.add(id);
    });
    return ids;
  }, [jobState?.data]);

  const visibleJobs = useMemo(
    () =>
      similarJobs
        .filter((job: { id?: number }) => !mainJobIds.has(Number(job?.id)))
        .slice(0, SIMILAR_JOBS_LIMIT),
    [similarJobs, mainJobIds]
  );

  const [hasRequested, setHasRequested] = useState(false);
  const settledRef = useRef(false);

  useEffect(() => {
    if (!token || hasRequested) return;

    setHasRequested(true);
    dispatch(
      similarJobsRequest({
        offset: 0,
        limit: SIMILAR_JOBS_FETCH_LIMIT,
      })
    );
  }, [dispatch, token, hasRequested]);

  useEffect(() => {
    if (!hasRequested || similarJobsLoading || settledRef.current) return;
    settledRef.current = true;
    onSettled?.();
  }, [hasRequested, similarJobsLoading, onSettled]);

  const loading = hold || !hasRequested || similarJobsLoading;

  if (!loading && visibleJobs.length === 0) {
    return null;
  }

  return (
    <Box className={`similar-jobs filter-open ${token === null ? "no-token" : ""}`}>
      {!loading && visibleJobs.length > 0 && (
        <Typography variant="h5">Similar Jobs</Typography>
      )}
      <Box className="similar-jobs-list">
        {loading ? (
          <div className="loading-wrapper">
            <SimilarJobLoading />
          </div>
        ) : (
          <>
            {visibleJobs.map((job: any) => (
              <JobCards
                key={job.id}
                job={job}
                noButton={true}
                jobHubDescription={true}
                isJobHub={isJobHub}
              />
            ))}
          </>
        )}
      </Box>
    </Box>
  );
};

export default SimilarJobs;
